import { formatSupabaseError } from './formatSupabaseError';
import { previewOpeningAckHtml } from './openingAckTemplate';
import { loadTicketEmailSignature, saveTicketEmailSignature } from './ticketEmailSignature';
import { getActivePortalKind, getDbSupabase, supabase } from './supabase';
import type {
  Ticket,
  TicketAgent,
  TicketAttachment,
  TicketComposeMode,
  TicketFolder,
  TicketMailbox,
  TicketMessage,
  TicketPriority,
  TicketSpamRule,
  TicketStatus,
  TicketStatusDefinition,
  TicketTagDefinition,
  TicketActivityEntry,
  TicketEmailTemplate,
  TicketNumberConfig,
} from '../types/tickets';

const TICKET_SELECT_BASE =
  'id,ticket_number,subject,status,priority,folder_id,mailbox_id,portal_kind,client_id,contact_id,assignee_email,requester_email,requester_name,conversation_key,is_unread,last_message_at,created_at,updated_at';

const TICKET_SELECT = `${TICKET_SELECT_BASE},last_follow_up_at`;

const MAILBOX_SELECT = 'id,address,display_name,transport,is_active,portal_kind';

function isMissingFollowUpColumn(error: unknown): boolean {
  return /last_follow_up_at|column/i.test(
    typeof error === 'string' ? error : formatSupabaseError(error as { message?: string }),
  );
}

function isMissingPortalKindColumn(error: unknown): boolean {
  return /portal_kind|column/i.test(
    typeof error === 'string' ? error : formatSupabaseError(error as { message?: string }),
  );
}

function demDb() {
  return getDbSupabase('crm');
}

/** Ticketing portal scope: Sales CRM vs IT Management. */
export type TicketPortalKind = 'crm' | 'it';

export function resolveTicketPortalKind(): TicketPortalKind {
  return getActivePortalKind() === 'it' ? 'it' : 'crm';
}

function isMissingMailboxIdColumn(error: unknown): boolean {
  return /mailbox_id|column/i.test(
    typeof error === 'string' ? error : formatSupabaseError(error as { message?: string }),
  );
}

/** First active mailbox for the current portal, else first mailbox for that portal. */
export async function resolvePortalMailboxId(): Promise<string | null> {
  const db = demDb();
  const portal = resolveTicketPortalKind();
  let active = await db
    .from('ticket_mailboxes')
    .select('id')
    .eq('portal_kind', portal)
    .eq('is_active', true)
    .order('address')
    .limit(1)
    .maybeSingle();
  if (active.error && isMissingPortalKindColumn(active.error)) {
    active = await db
      .from('ticket_mailboxes')
      .select('id')
      .eq('is_active', true)
      .order('address')
      .limit(1)
      .maybeSingle();
    if (active.error) return null;
    if (active.data?.id) return String(active.data.id);
    const any = await db.from('ticket_mailboxes').select('id').order('address').limit(1).maybeSingle();
    return any.data?.id ? String(any.data.id) : null;
  }
  if (active.error) return null;
  if (active.data?.id) return String(active.data.id);
  const anyPortal = await db
    .from('ticket_mailboxes')
    .select('id')
    .eq('portal_kind', portal)
    .order('address')
    .limit(1)
    .maybeSingle();
  if (anyPortal.error) return null;
  return anyPortal.data?.id ? String(anyPortal.data.id) : null;
}

const SYSTEM_STATUS_SEED: Array<{
  slug: string;
  name: string;
  color: string;
  sort_order: number;
  is_closed: boolean;
}> = [
  { slug: 'open', name: 'Open', color: '#2563eb', sort_order: 10, is_closed: false },
  { slug: 'pending', name: 'Pending', color: '#d97706', sort_order: 20, is_closed: false },
  { slug: 'processing', name: 'Processing', color: '#7c3aed', sort_order: 30, is_closed: false },
  { slug: 'on_hold', name: 'On hold', color: '#64748b', sort_order: 40, is_closed: false },
  { slug: 'closed', name: 'Closed', color: '#15803d', sort_order: 50, is_closed: true },
  { slug: 'spam', name: 'Spam', color: '#b91c1c', sort_order: 90, is_closed: true },
];

/** Seed system statuses + workflow settings for a new mailbox. No-op if migration not applied. */
export async function seedMailboxDefaults(mailboxId: string): Promise<void> {
  const db = demDb();
  const statusRows = SYSTEM_STATUS_SEED.map((s) => ({
    mailbox_id: mailboxId,
    slug: s.slug,
    name: s.name,
    color: s.color,
    sort_order: s.sort_order,
    is_closed: s.is_closed,
    is_active: true,
    is_system: true,
  }));
  const st = await db
    .from('ticket_status_definitions')
    .upsert(statusRows, { onConflict: 'mailbox_id,slug', ignoreDuplicates: true });
  if (st.error && isMissingMailboxIdColumn(st.error)) return;

  let setErr = (
    await db.from('ticket_settings').upsert(
      {
        mailbox_id: mailboxId,
        auto_reopen_on_inbound: true,
        follow_up_workdays: 2,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'mailbox_id', ignoreDuplicates: true },
    )
  ).error;
  if (setErr && isMissingMailboxIdColumn(setErr)) return;
  if (setErr && /null value.*id|id.*not-null|column.*id/i.test(formatSupabaseError(setErr))) {
    const maxRow = await db
      .from('ticket_settings')
      .select('id')
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();
    const nextId = Number(maxRow.data?.id ?? 0) + 1;
    setErr = (
      await db.from('ticket_settings').upsert(
        {
          id: nextId,
          mailbox_id: mailboxId,
          auto_reopen_on_inbound: true,
          follow_up_workdays: 2,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'mailbox_id', ignoreDuplicates: true },
      )
    ).error;
  }
  void setErr;
}

export async function checkIsTicketAgent(): Promise<boolean> {
  const db = demDb();
  const { data, error } = await db.rpc('is_ticket_agent');
  if (error) {
    // Fallback: try reading folders — RLS will deny non-agents
    const probe = await db.from('ticket_folders').select('id').limit(1);
    if (probe.error) return false;
    return true;
  }
  return Boolean(data);
}

export type TicketFolderNode = TicketFolder & { children: TicketFolderNode[] };

const FOOTER_FOLDER_SLUGS = new Set(['spam', 'trash']);

export function isTicketFooterFolder(folder: { slug: string }): boolean {
  return FOOTER_FOLDER_SLUGS.has(folder.slug);
}

export function isTicketMainSystemFolder(folder: { slug: string }): boolean {
  return folder.slug === 'inbox' || folder.slug === 'sent' || isTicketFooterFolder(folder);
}

export function buildTicketFolderTree(folders: TicketFolder[]): TicketFolderNode[] {
  const byParent = new Map<string | null, TicketFolder[]>();
  for (const folder of folders) {
    const key = folder.parent_id ?? null;
    const list = byParent.get(key) ?? [];
    list.push(folder);
    byParent.set(key, list);
  }
  const walk = (parentId: string | null): TicketFolderNode[] =>
    (byParent.get(parentId) ?? []).map((folder) => ({
      ...folder,
      children: walk(folder.id),
    }));
  return walk(null);
}

const MAIN_HEAD_ORDER = ['inbox', 'sent'];

export function splitTicketFolderTree(folders: TicketFolder[]): {
  main: TicketFolderNode[];
  footer: TicketFolderNode[];
} {
  const main: TicketFolderNode[] = [];
  const footer: TicketFolderNode[] = [];
  for (const node of buildTicketFolderTree(folders)) {
    if (isTicketFooterFolder(node)) footer.push(node);
    else main.push(node);
  }
  const rank = (slug: string) => {
    const head = MAIN_HEAD_ORDER.indexOf(slug);
    if (head >= 0) return head;
    return 100;
  };
  main.sort((a, b) => {
    const ra = rank(a.slug);
    const rb = rank(b.slug);
    if (ra !== rb) return ra - rb;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name);
  });
  footer.sort((a, b) => {
    const order = (slug: string) => (slug === 'spam' ? 0 : slug === 'trash' ? 1 : 9);
    return order(a.slug) - order(b.slug);
  });
  return { main, footer };
}

export function flattenTicketFolders(
  folders: TicketFolder[],
): Array<TicketFolder & { depth: number }> {
  const out: Array<TicketFolder & { depth: number }> = [];
  const walk = (nodes: TicketFolderNode[], depth: number) => {
    for (const node of nodes) {
      out.push({ ...node, depth });
      walk(node.children, depth + 1);
    }
  };
  const { main, footer } = splitTicketFolderTree(folders);
  walk(main, 0);
  walk(footer, 0);
  return out;
}

function isMissingFolderPortalKindColumn(error: unknown): boolean {
  return /portal_kind|column/i.test(
    typeof error === 'object' && error && 'message' in error
      ? String((error as { message?: string }).message ?? '')
      : String(error ?? ''),
  );
}

const FOLDER_SELECT = 'id,mailbox_id,slug,name,is_system,sort_order,parent_id,portal_kind';
const FOLDER_SELECT_LEGACY = 'id,mailbox_id,slug,name,is_system,sort_order,parent_id';

async function resolveSystemFolderId(slug: string): Promise<string> {
  const db = demDb();
  const portal = resolveTicketPortalKind();
  let { data, error } = await db
    .from('ticket_folders')
    .select('id')
    .is('mailbox_id', null)
    .is('parent_id', null)
    .eq('slug', slug)
    .eq('portal_kind', portal)
    .maybeSingle();
  if (error && isMissingFolderPortalKindColumn(error)) {
    ({ data, error } = await db
      .from('ticket_folders')
      .select('id')
      .is('mailbox_id', null)
      .eq('slug', slug)
      .maybeSingle());
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (!data?.id) throw new Error(`${slug} folder not found.`);
  return String(data.id);
}

function mapTicketFolder(row: Record<string, unknown>): TicketFolder {
  return {
    id: String(row.id),
    mailbox_id: row.mailbox_id ? String(row.mailbox_id) : null,
    slug: String(row.slug ?? ''),
    name: String(row.name ?? ''),
    is_system: Boolean(row.is_system),
    sort_order: Number(row.sort_order ?? 0),
    parent_id: row.parent_id ? String(row.parent_id) : null,
    portal_kind: row.portal_kind === 'it' ? 'it' : 'crm',
  };
}

export async function fetchTicketFolders(): Promise<TicketFolder[]> {
  const db = demDb();
  const portal = resolveTicketPortalKind();
  const withPortal = await db
    .from('ticket_folders')
    .select(FOLDER_SELECT)
    .eq('portal_kind', portal)
    .order('sort_order', { ascending: true });
  if (!withPortal.error) {
    return (withPortal.data ?? []).map((row) => mapTicketFolder(row as Record<string, unknown>));
  }
  if (!isMissingFolderPortalKindColumn(withPortal.error)) {
    throw new Error(formatSupabaseError(withPortal.error));
  }
  const withParent = await db
    .from('ticket_folders')
    .select(FOLDER_SELECT_LEGACY)
    .order('sort_order', { ascending: true });
  if (!withParent.error) {
    return (withParent.data ?? []).map((row) => mapTicketFolder(row as Record<string, unknown>));
  }
  const { data, error } = await db
    .from('ticket_folders')
    .select('id,mailbox_id,slug,name,is_system,sort_order')
    .order('sort_order', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => mapTicketFolder(row as Record<string, unknown>));
}

/** Custom root folders belong under Inbox (not beside Sent). IT starts with no custom folders. */
export async function ensureCustomFoldersUnderInbox(): Promise<TicketFolder[]> {
  const folders = await fetchTicketFolders();
  if (resolveTicketPortalKind() === 'it') return folders;
  const inbox =
    folders.find((f) => f.slug === 'inbox' && !f.parent_id) ??
    folders.find((f) => f.slug === 'inbox');
  if (!inbox) return folders;

  const orphans = folders.filter(
    (f) =>
      !f.parent_id &&
      f.slug !== 'inbox' &&
      f.slug !== 'sent' &&
      !isTicketFooterFolder(f),
  );
  if (orphans.length === 0) return folders;

  const db = demDb();
  for (const orphan of orphans) {
    const { error } = await db
      .from('ticket_folders')
      .update({ parent_id: inbox.id })
      .eq('id', orphan.id);
    if (error && !/parent_id|column/i.test(formatSupabaseError(error))) {
      throw new Error(formatSupabaseError(error));
    }
  }
  return fetchTicketFolders();
}

export async function fetchTicketFolderUnreadCounts(): Promise<Record<string, number>> {
  const db = demDb();
  const portal = resolveTicketPortalKind();
  let query = db.from('tickets').select('folder_id').eq('is_unread', true);
  query = query.eq('portal_kind', portal);
  let { data, error } = await query;
  if (error && isMissingPortalKindColumn(error)) {
    ({ data, error } = await db.from('tickets').select('folder_id').eq('is_unread', true));
  }
  if (error) throw new Error(formatSupabaseError(error));
  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    const id = String((row as { folder_id?: string }).folder_id ?? '');
    if (!id) continue;
    counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

export async function fetchTicketUnreadBadgeCount(): Promise<number> {
  const [folders, counts] = await Promise.all([
    fetchTicketFolders(),
    fetchTicketFolderUnreadCounts(),
  ]);
  const skip = new Set(folders.filter((f) => isTicketFooterFolder(f)).map((f) => f.id));
  let total = 0;
  for (const [id, n] of Object.entries(counts)) {
    if (skip.has(id)) continue;
    total += n;
  }
  return total;
}

export async function fetchTickets(options: {
  folderId?: string;
  /** Sent folder: also include tickets that have any outbound reply (still living in Inbox). */
  sentInclusive?: boolean;
  excludeFolderIds?: string[];
  status?: TicketStatus | 'all';
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ rows: Ticket[]; total: number }> {
  const page = Math.max(1, options.page ?? 1);
  const pageSize = Math.min(100, Math.max(20, options.pageSize ?? 40));
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const search = options.search?.trim() ?? '';

  const db = demDb();

  let outboundTicketIds: string[] = [];
  if (options.sentInclusive && options.folderId) {
    const { data: outboundRows, error: outboundError } = await db
      .from('ticket_messages')
      .select('ticket_id')
      .eq('direction', 'outbound');
    if (outboundError) throw new Error(formatSupabaseError(outboundError));
    outboundTicketIds = [
      ...new Set(
        (outboundRows ?? [])
          .map((row) => String((row as { ticket_id?: string }).ticket_id ?? '').trim())
          .filter(Boolean),
      ),
    ];
  }

  const portal = resolveTicketPortalKind();
  const selectBaseNoPortal = TICKET_SELECT_BASE.replace(',portal_kind', '');
  const selectFullNoPortal = `${selectBaseNoPortal},last_follow_up_at`;

  const run = async (selectCols: string, withPortal: boolean) => {
    let query = db.from('tickets').select(selectCols, { count: 'exact' });
    if (withPortal) {
      query = query.eq('portal_kind', portal);
    }
    if (options.folderId) {
      if (options.sentInclusive) {
        if (outboundTicketIds.length > 0) {
          const idList = outboundTicketIds.slice(0, 200).join(',');
          query = query.or(`folder_id.eq.${options.folderId},id.in.(${idList})`);
        } else {
          query = query.eq('folder_id', options.folderId);
        }
        const exclude = (options.excludeFolderIds ?? []).filter(Boolean);
        if (exclude.length > 0) {
          query = query.not('folder_id', 'in', `(${exclude.join(',')})`);
        }
      } else {
        query = query.eq('folder_id', options.folderId);
      }
    }
    if (options.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }
    if (search) {
      const escaped = search.replace(/[%_]/g, '\\$&');
      query = query.or(
        `ticket_number.ilike.%${escaped}%,subject.ilike.%${escaped}%,requester_email.ilike.%${escaped}%,requester_name.ilike.%${escaped}%`,
      );
    }
    return query.order('last_message_at', { ascending: false }).range(from, to);
  };

  let { data, error, count } = await run(TICKET_SELECT, true);
  if (error && isMissingPortalKindColumn(error)) {
    ({ data, error, count } = await run(selectFullNoPortal, false));
  }
  if (error && isMissingFollowUpColumn(error)) {
    ({ data, error, count } = await run(TICKET_SELECT_BASE, true));
    if (error && isMissingPortalKindColumn(error)) {
      ({ data, error, count } = await run(selectBaseNoPortal, false));
    }
  }
  if (error) throw new Error(formatSupabaseError(error));

  return { rows: (data ?? []) as unknown as Ticket[], total: count ?? 0 };
}

export async function fetchTicket(id: string): Promise<Ticket | null> {
  const db = demDb();
  const portal = resolveTicketPortalKind();
  let { data, error } = await db
    .from('tickets')
    .select(TICKET_SELECT)
    .eq('id', id)
    .eq('portal_kind', portal)
    .maybeSingle();
  if (error && isMissingPortalKindColumn(error)) {
    ({ data, error } = await db.from('tickets').select(TICKET_SELECT.replace(',portal_kind', '')).eq('id', id).maybeSingle());
  }
  if (error && isMissingFollowUpColumn(error)) {
    ({ data, error } = await db
      .from('tickets')
      .select(TICKET_SELECT_BASE)
      .eq('id', id)
      .eq('portal_kind', portal)
      .maybeSingle());
    if (error && isMissingPortalKindColumn(error)) {
      ({ data, error } = await db
        .from('tickets')
        .select(TICKET_SELECT_BASE.replace(',portal_kind', ''))
        .eq('id', id)
        .maybeSingle());
    }
  }
  if (error) throw new Error(formatSupabaseError(error));
  return (data as unknown as Ticket | null) ?? null;
}

export async function fetchTicketMessages(ticketId: string): Promise<TicketMessage[]> {
  const db = demDb();
  const cols =
    'id,ticket_id,direction,from_email,from_name,to_emails,cc_emails,subject,body_html,body_text,provider_message_id,internet_message_id,in_reply_to,references_header,sent_at,created_by_email,created_at';
  const withSoft = await db
    .from('ticket_messages')
    .select(cols)
    .eq('ticket_id', ticketId)
    .is('deleted_at', null)
    .order('sent_at', { ascending: true });
  if (!withSoft.error) {
    return (withSoft.data ?? []) as TicketMessage[];
  }
  if (!/deleted_at|column/i.test(formatSupabaseError(withSoft.error))) {
    throw new Error(formatSupabaseError(withSoft.error));
  }
  const { data, error } = await db
    .from('ticket_messages')
    .select(cols)
    .eq('ticket_id', ticketId)
    .order('sent_at', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketMessage[];
}

/** Avoid remounting message HTML (and restarting GIFs) when reload returns the same bodies. */
export function sameTicketMessages(a: TicketMessage[], b: TicketMessage[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i]!;
    const right = b[i]!;
    if (
      left.id !== right.id ||
      left.body_html !== right.body_html ||
      left.body_text !== right.body_text ||
      left.sent_at !== right.sent_at ||
      left.subject !== right.subject
    ) {
      return false;
    }
  }
  return true;
}

export async function fetchTicketAttachments(messageIds: string[]): Promise<TicketAttachment[]> {
  if (messageIds.length === 0) return [];
  const db = demDb();
  const fullSelect =
    'id,message_id,storage_path,file_name,content_type,size_bytes,is_inline,content_id,created_at';
  const baseSelect = 'id,message_id,storage_path,file_name,content_type,size_bytes,created_at';

  const primary = await db.from('ticket_attachments').select(fullSelect).in('message_id', messageIds);
  if (primary.error && /is_inline|content_id|column/.test(formatSupabaseError(primary.error))) {
    const fallback = await db.from('ticket_attachments').select(baseSelect).in('message_id', messageIds);
    if (fallback.error) throw new Error(formatSupabaseError(fallback.error));
    return (fallback.data ?? []) as TicketAttachment[];
  }
  if (primary.error) throw new Error(formatSupabaseError(primary.error));
  return (primary.data ?? []) as TicketAttachment[];
}

function isImageTicketAttachment(att: TicketAttachment): boolean {
  const ct = (att.content_type || '').toLowerCase();
  if (ct.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(att.file_name || '');
}

function isDownloadableDocumentAttachment(att: TicketAttachment): boolean {
  const ct = (att.content_type || '').toLowerCase();
  if (ct === 'application/pdf') return true;
  if (ct.includes('officedocument') || ct.includes('msword') || ct.includes('ms-excel')) return true;
  if (ct.includes('spreadsheet') || ct.includes('presentation')) return true;
  return /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z)$/i.test(att.file_name || '');
}

/** Signature / body images shown inside the email — not downloadable file attachments. */
export function isInlineDisplayedAttachment(att: TicketAttachment, bodyHtml = ''): boolean {
  // PDFs and documents must always appear in the attachment list (Exchange often sets is_inline + content_id).
  if (isDownloadableDocumentAttachment(att)) return false;
  if (!isImageTicketAttachment(att)) return false;

  if (att.is_inline) return true;

  const cid = String(att.content_id ?? '')
    .trim()
    .replace(/^<|>$/g, '');
  if (cid) return true;

  const path = att.storage_path || '';
  if (/inline_/i.test(path)) return true;

  const name = att.file_name || '';
  if (/^(bth-inline-|inline-|image\d{3,}\.|att\d{3,}\.)/i.test(name)) return true;

  const html = bodyHtml || '';
  if (!html) return false;

  const htmlLower = html.toLowerCase();
  const nameLower = name.toLowerCase();
  if (path && html.includes(path)) return true;
  if (nameLower && htmlLower.includes(nameLower)) return true;
  const stem = nameLower.replace(/\.[a-z0-9]+$/, '');
  if (stem.length >= 4 && htmlLower.includes(`cid:${stem}`)) return true;
  return false;
}

export function regularFileAttachments(
  attachments: TicketAttachment[],
  bodyHtml = '',
): TicketAttachment[] {
  return attachments.filter((att) => !isInlineDisplayedAttachment(att, bodyHtml));
}

export function messageNeedsAttachmentRefresh(
  messages: TicketMessage[],
  attachmentsByMessage: Record<string, TicketAttachment[]>,
): boolean {
  for (const msg of messages) {
    const html = msg.body_html ?? '';
    const atts = attachmentsByMessage[msg.id] ?? [];
    // Unresolved Graph inline images only — plain https <img> must not trigger refresh loops.
    if (/cid:/i.test(html)) return true;
    // Inbound with a Graph id but nothing stored yet — may still have PDF/file attachments.
    if (msg.direction === 'inbound' && atts.length === 0 && msg.provider_message_id) {
      return true;
    }
  }
  return false;
}

export async function refreshTicketAttachments(
  ticketId: string,
  force = false,
): Promise<{ refreshed: number; attachments_saved: number; errors?: string[] }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in.');

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-ticket-inbox-sync`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ticket_id: ticketId,
      refresh_attachments: true,
      force,
    }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    refreshed?: number;
    attachments_saved?: number;
    errors?: string[];
    error?: string;
  };
  if (!res.ok || payload.ok === false) {
    throw new Error(String(payload.error ?? payload.errors?.[0] ?? `Refresh failed (HTTP ${res.status})`));
  }
  return {
    refreshed: payload.refreshed ?? 0,
    attachments_saved: payload.attachments_saved ?? 0,
    errors: payload.errors,
  };
}

export async function createTicketFolder(
  name: string,
  parentId?: string | null,
): Promise<TicketFolder> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Folder name is required.');
  const baseSlug = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  if (!baseSlug) throw new Error('Folder name must contain letters or numbers.');

  const db = demDb();
  let parent = parentId?.trim() || null;
  if (!parent) {
    const folders = await fetchTicketFolders();
    const inbox =
      folders.find((f) => f.slug === 'inbox' && !f.parent_id) ??
      folders.find((f) => f.slug === 'inbox');
    parent = inbox?.id ?? null;
  }
  if (parent) {
    const folders = await fetchTicketFolders();
    const parentFolder = folders.find((f) => f.id === parent);
    if (parentFolder && isTicketFooterFolder(parentFolder)) {
      throw new Error('Spam and Trash cannot have subfolders.');
    }
  }
  let siblingQuery = db.from('ticket_folders').select('sort_order');
  siblingQuery = parent
    ? siblingQuery.eq('parent_id', parent)
    : siblingQuery.is('parent_id', null);
  const { data: existing } = await siblingQuery
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const sortOrder = Number(existing?.sort_order ?? 0) + 1;

  for (let attempt = 0; attempt < 12; attempt += 1) {
    const slug = attempt === 0 ? baseSlug : `${baseSlug}_${attempt + 1}`.slice(0, 40);
    const portal = resolveTicketPortalKind();
    const insertRow: Record<string, unknown> = {
      mailbox_id: null,
      slug,
      name: trimmed,
      is_system: false,
      sort_order: sortOrder,
      parent_id: parent,
      portal_kind: portal,
    };
    let { data, error } = await db
      .from('ticket_folders')
      .insert(insertRow)
      .select(FOLDER_SELECT)
      .maybeSingle();
    if (error && isMissingFolderPortalKindColumn(error)) {
      const { portal_kind: _drop, ...legacy } = insertRow;
      ({ data, error } = await db
        .from('ticket_folders')
        .insert(legacy)
        .select(FOLDER_SELECT_LEGACY)
        .maybeSingle());
    }
    if (!error && data) return mapTicketFolder(data as Record<string, unknown>);
    if (error && (error.code === '23505' || /duplicate|unique/i.test(error.message))) continue;
    if (error) throw new Error(formatSupabaseError(error));
  }
  throw new Error('Could not create a unique folder slug. Try a different name.');
}

export async function deleteTicketFolder(folderId: string): Promise<{ inboxId: string }> {
  const folders = await fetchTicketFolders();
  const target = folders.find((f) => f.id === folderId);
  if (!target) throw new Error('Folder not found.');
  if (target.is_system) throw new Error('System folders cannot be deleted.');

  const ids = new Set<string>([folderId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const folder of folders) {
      if (folder.parent_id && ids.has(folder.parent_id) && !ids.has(folder.id)) {
        ids.add(folder.id);
        grew = true;
      }
    }
  }

  const inbox = folders.find((f) => f.slug === 'inbox' && !f.parent_id) ?? folders.find((f) => f.slug === 'inbox');
  if (!inbox) throw new Error('Inbox folder not found.');

  const db = demDb();
  const { error: moveErr } = await db
    .from('tickets')
    .update({ folder_id: inbox.id })
    .in('folder_id', [...ids]);
  if (moveErr) throw new Error(formatSupabaseError(moveErr));

  const { error: delErr } = await db.from('ticket_folders').delete().eq('id', folderId);
  if (delErr) throw new Error(formatSupabaseError(delErr));
  return { inboxId: inbox.id };
}

export async function fetchTicketTagDefinitions(mailboxId: string): Promise<TicketTagDefinition[]> {
  const db = demDb();
  const scoped = await db
    .from('ticket_tag_definitions')
    .select('id,mailbox_id,name,color,sort_order,is_active,created_at')
    .eq('mailbox_id', mailboxId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (!scoped.error) return (scoped.data ?? []) as TicketTagDefinition[];
  if (!isMissingMailboxIdColumn(scoped.error)) throw new Error(formatSupabaseError(scoped.error));
  const { data, error } = await db
    .from('ticket_tag_definitions')
    .select('id,name,color,sort_order,is_active,created_at')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketTagDefinition[];
}

export async function fetchAllTicketTagDefinitions(mailboxId: string): Promise<TicketTagDefinition[]> {
  const db = demDb();
  const scoped = await db
    .from('ticket_tag_definitions')
    .select('id,mailbox_id,name,color,sort_order,is_active,created_at')
    .eq('mailbox_id', mailboxId)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (!scoped.error) return (scoped.data ?? []) as TicketTagDefinition[];
  if (!isMissingMailboxIdColumn(scoped.error)) throw new Error(formatSupabaseError(scoped.error));
  const { data, error } = await db
    .from('ticket_tag_definitions')
    .select('id,name,color,sort_order,is_active,created_at')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketTagDefinition[];
}

export async function upsertTicketTagDefinition(input: {
  id?: string;
  mailbox_id: string;
  name: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}): Promise<TicketTagDefinition> {
  const db = demDb();
  const row = {
    name: input.name.trim(),
    color: (input.color ?? '#6b7280').trim(),
    sort_order: input.sort_order ?? 0,
    is_active: input.is_active ?? true,
    mailbox_id: input.mailbox_id,
    ...(input.id ? { id: input.id } : {}),
  };
  let { data, error } = await db
    .from('ticket_tag_definitions')
    .upsert(row, { onConflict: 'id' })
    .select('id,mailbox_id,name,color,sort_order,is_active,created_at')
    .maybeSingle();
  if (error && isMissingMailboxIdColumn(error)) {
    const { mailbox_id: _drop, ...legacy } = row;
    ({ data, error } = await db
      .from('ticket_tag_definitions')
      .upsert(legacy, { onConflict: 'id' })
      .select('id,name,color,sort_order,is_active,created_at')
      .maybeSingle());
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Tag save returned no rows.');
  return data as TicketTagDefinition;
}

export async function deleteTicketTagDefinition(id: string): Promise<void> {
  const db = demDb();
  const { error } = await db.from('ticket_tag_definitions').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchTicketTagIds(ticketId: string): Promise<string[]> {
  const db = demDb();
  const { data, error } = await db
    .from('ticket_tag_links')
    .select('tag_id')
    .eq('ticket_id', ticketId);
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => String((row as { tag_id: string }).tag_id));
}

export async function setTicketTags(ticketId: string, tagIds: string[]): Promise<void> {
  const db = demDb();
  const { error: delErr } = await db.from('ticket_tag_links').delete().eq('ticket_id', ticketId);
  if (delErr) throw new Error(formatSupabaseError(delErr));
  if (tagIds.length === 0) return;
  const { error } = await db.from('ticket_tag_links').insert(
    tagIds.map((tag_id) => ({ ticket_id: ticketId, tag_id })),
  );
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchTicketStatusDefinitions(
  mailboxId?: string,
): Promise<TicketStatusDefinition[]> {
  const db = demDb();
  const id = mailboxId ?? (await resolvePortalMailboxId());
  if (id) {
    const scoped = await db
      .from('ticket_status_definitions')
      .select('id,mailbox_id,slug,name,color,sort_order,is_active,is_closed,is_system,created_at')
      .eq('mailbox_id', id)
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });
    if (!scoped.error) return (scoped.data ?? []) as TicketStatusDefinition[];
    if (!isMissingMailboxIdColumn(scoped.error)) throw new Error(formatSupabaseError(scoped.error));
  }
  const { data, error } = await db
    .from('ticket_status_definitions')
    .select('id,slug,name,color,sort_order,is_active,is_closed,is_system,created_at')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketStatusDefinition[];
}

export async function fetchAllTicketStatusDefinitions(
  mailboxId: string,
): Promise<TicketStatusDefinition[]> {
  const db = demDb();
  const scoped = await db
    .from('ticket_status_definitions')
    .select('id,mailbox_id,slug,name,color,sort_order,is_active,is_closed,is_system,created_at')
    .eq('mailbox_id', mailboxId)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (!scoped.error) return (scoped.data ?? []) as TicketStatusDefinition[];
  if (!isMissingMailboxIdColumn(scoped.error)) throw new Error(formatSupabaseError(scoped.error));
  const { data, error } = await db
    .from('ticket_status_definitions')
    .select('id,slug,name,color,sort_order,is_active,is_closed,is_system,created_at')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketStatusDefinition[];
}

export async function upsertTicketStatusDefinition(input: {
  id?: string;
  mailbox_id: string;
  slug: string;
  name: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
  is_closed?: boolean;
}): Promise<TicketStatusDefinition> {
  const slug = input.slug
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  if (!slug) throw new Error('Status slug is required.');
  const db = demDb();
  const row = {
    slug,
    name: input.name.trim(),
    color: (input.color ?? '#6b7280').trim(),
    sort_order: input.sort_order ?? 0,
    is_active: input.is_active ?? true,
    is_closed: input.is_closed ?? false,
    mailbox_id: input.mailbox_id,
    ...(input.id ? { id: input.id } : {}),
  };
  let { data, error } = await db
    .from('ticket_status_definitions')
    .upsert(row, { onConflict: 'id' })
    .select('id,mailbox_id,slug,name,color,sort_order,is_active,is_closed,is_system,created_at')
    .maybeSingle();
  if (error && isMissingMailboxIdColumn(error)) {
    const { mailbox_id: _drop, ...legacy } = row;
    ({ data, error } = await db
      .from('ticket_status_definitions')
      .upsert(legacy, { onConflict: 'id' })
      .select('id,slug,name,color,sort_order,is_active,is_closed,is_system,created_at')
      .maybeSingle());
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Status save returned no rows.');
  return data as TicketStatusDefinition;
}

export async function deleteTicketStatusDefinition(id: string): Promise<void> {
  const db = demDb();
  const { data: row } = await db
    .from('ticket_status_definitions')
    .select('is_system,slug')
    .eq('id', id)
    .maybeSingle();
  if (row?.is_system) throw new Error('System statuses cannot be deleted.');
  const { count } = await db
    .from('tickets')
    .select('id', { count: 'exact', head: true })
    .eq('status', String(row?.slug ?? ''));
  if ((count ?? 0) > 0) {
    throw new Error('Status is in use by tickets. Deactivate it instead.');
  }
  const { error } = await db.from('ticket_status_definitions').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchTicketActivityLog(ticketId: string): Promise<TicketActivityEntry[]> {
  const db = demDb();
  const { data, error } = await db
    .from('ticket_activity_log')
    .select('id,ticket_id,actor_email,action,detail,created_at')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketActivityEntry[];
}

export async function addTicketNote(
  ticketId: string,
  bodyHtml: string,
  actorEmail: string,
): Promise<TicketMessage> {
  const trimmed = bodyHtml.trim();
  if (!trimmed || trimmed === '<p></p>') throw new Error('Note body is required.');
  const db = demDb();
  const text = trimmed.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const { data, error } = await db
    .from('ticket_messages')
    .insert({
      ticket_id: ticketId,
      direction: 'note',
      from_email: actorEmail.trim().toLowerCase(),
      from_name: '',
      to_emails: [],
      cc_emails: [],
      subject: '',
      body_html: trimmed,
      body_text: text,
      provider_message_id: '',
      internet_message_id: '',
      sent_at: new Date().toISOString(),
      created_by_email: actorEmail,
    })
    .select(
      'id,ticket_id,direction,from_email,from_name,to_emails,cc_emails,subject,body_html,body_text,provider_message_id,internet_message_id,in_reply_to,references_header,sent_at,created_by_email,created_at',
    )
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Note insert returned no rows.');
  await logTicketActivity(ticketId, 'internal_note', { preview: text.slice(0, 120) }, actorEmail);
  return data as TicketMessage;
}

export async function updateTicketNote(
  messageId: string,
  bodyHtml: string,
): Promise<TicketMessage> {
  const trimmed = bodyHtml.trim();
  if (!trimmed || trimmed === '<p></p>') throw new Error('Note body is required.');
  const db = demDb();
  const text = trimmed.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const { data, error } = await db
    .from('ticket_messages')
    .update({
      body_html: trimmed,
      body_text: text,
    })
    .eq('id', messageId)
    .eq('direction', 'note')
    .select(
      'id,ticket_id,direction,from_email,from_name,to_emails,cc_emails,subject,body_html,body_text,provider_message_id,internet_message_id,in_reply_to,references_header,sent_at,created_by_email,created_at',
    )
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Note not found.');
  return data as TicketMessage;
}

export async function deleteTicketMessage(
  messageId: string,
  actorEmail: string,
): Promise<void> {
  const db = demDb();
  const { data: msg, error: fetchErr } = await db
    .from('ticket_messages')
    .select('id,ticket_id,subject,from_email,direction,provider_message_id,internet_message_id')
    .eq('id', messageId)
    .maybeSingle();
  if (fetchErr) throw new Error(formatSupabaseError(fetchErr));
  if (!msg?.id) throw new Error('Message not found.');

  const ticketId = String(msg.ticket_id);

  // Soft-delete so Graph inbox sync does not re-create the same email.
  const soft = await db
    .from('ticket_messages')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', messageId);
  if (soft.error && /deleted_at|column/i.test(formatSupabaseError(soft.error))) {
    const { data: atts } = await db
      .from('ticket_attachments')
      .select('storage_path')
      .eq('message_id', messageId);
    const paths = (atts ?? []).map((a) => String(a.storage_path)).filter(Boolean);
    if (paths.length > 0) {
      await db.storage.from('ticket-attachments').remove(paths).catch(() => undefined);
    }
    const { error } = await db.from('ticket_messages').delete().eq('id', messageId);
    if (error) throw new Error(formatSupabaseError(error));
  } else if (soft.error) {
    throw new Error(formatSupabaseError(soft.error));
  }

  await logTicketActivity(
    ticketId,
    'delete_message',
    {
      message_id: messageId,
      subject: msg.subject,
      from_email: msg.from_email,
      direction: msg.direction,
      provider_message_id: msg.provider_message_id,
      internet_message_id: msg.internet_message_id,
    },
    actorEmail,
  );
}

export async function upsertSpamRule(input: {
  id?: string;
  mailbox_id: string;
  rule_type: TicketSpamRule['rule_type'];
  pattern: string;
  action: TicketSpamRule['action'];
  is_active?: boolean;
  created_by_email?: string;
}): Promise<TicketSpamRule> {
  const db = demDb();
  const row = {
    rule_type: input.rule_type,
    pattern: input.pattern.trim().toLowerCase(),
    action: input.action,
    is_active: input.is_active ?? true,
    mailbox_id: input.mailbox_id,
    ...(input.id ? { id: input.id } : {}),
    ...(input.created_by_email ? { created_by_email: input.created_by_email } : {}),
  };
  let { data, error } = await db
    .from('ticket_spam_rules')
    .upsert(row, { onConflict: 'id' })
    .select('id,mailbox_id,rule_type,pattern,action,is_active,created_by_email,created_at')
    .maybeSingle();
  if (error && isMissingMailboxIdColumn(error)) {
    const { mailbox_id: _drop, ...legacy } = row;
    ({ data, error } = await db
      .from('ticket_spam_rules')
      .upsert(legacy, { onConflict: 'id' })
      .select('id,rule_type,pattern,action,is_active,created_by_email,created_at')
      .maybeSingle());
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Spam rule save returned no rows.');
  return data as TicketSpamRule;
}

export async function deleteSpamRule(id: string): Promise<void> {
  const db = demDb();
  const { error } = await db.from('ticket_spam_rules').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function setSpamRuleActive(id: string, isActive: boolean): Promise<void> {
  const db = demDb();
  const { error } = await db.from('ticket_spam_rules').update({ is_active: isActive }).eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

async function resolveTicketMessageHtmlWithUrls(
  html: string,
  attachments: TicketAttachment[],
  getUrl: (path: string) => Promise<string>,
): Promise<string> {
  if (!html.trim()) return html;
  let out = html;
  const urlByPath = new Map<string, string>();

  for (const match of html.matchAll(/ticket-inline:\/\/([^"'\s>)]+)/gi)) {
    const path = String(match[1] ?? '').trim();
    if (!path || urlByPath.has(path)) continue;
    try {
      urlByPath.set(path, await getUrl(path));
    } catch {
      /* skip broken inline image */
    }
  }

  for (const att of attachments) {
    if (!att.storage_path) continue;
    try {
      urlByPath.set(att.storage_path, await getUrl(att.storage_path));
    } catch {
      /* skip broken attachment */
    }
  }

  for (const [path, url] of urlByPath) {
    out = out.split(`ticket-inline://${path}`).join(url);
  }

  for (const att of attachments) {
    if (!att.content_id) continue;
    const url = urlByPath.get(att.storage_path);
    if (!url) continue;
    const base = att.content_id.split('@')[0] ?? att.content_id;
    for (const token of [att.content_id, base]) {
      if (!token) continue;
      out = out.replace(new RegExp(`cid:${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), url);
    }
  }

  // Match remaining cid: tokens (Outlook format e.g. cid:image001.png@01D...)
  const cidMatches = [...out.matchAll(/cid:([^"'\s>)]+)/gi)];
  const inlineUrls = attachments
    .filter((a) => a.is_inline || a.storage_path.includes('/inline_'))
    .map((a) => urlByPath.get(a.storage_path))
    .filter((u): u is string => Boolean(u));

  let inlineIdx = 0;
  for (const match of cidMatches) {
    const cid = match[1] ?? '';
    if (!cid || !out.includes(`cid:${cid}`)) continue;

    const byName = attachments.find((a) => {
      const url = urlByPath.get(a.storage_path);
      if (!url) return false;
      const name = a.file_name.toLowerCase();
      const cidLower = cid.toLowerCase();
      return cidLower.includes(name.replace(/\.[^.]+$/, '')) || name.includes(cidLower.split('@')[0] ?? '');
    });
    const url = byName
      ? urlByPath.get(byName.storage_path)
      : inlineUrls[inlineIdx] ?? inlineUrls[0];
    if (!url) continue;
    out = out.replace(new RegExp(`cid:${cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), url);
    if (!byName) inlineIdx += 1;
  }

  return out;
}

/** Resolve ticket-inline:// paths and cid: references for in-app display (blob URLs). */
export async function resolveTicketMessageHtml(
  html: string,
  attachments: TicketAttachment[],
): Promise<string> {
  return resolveTicketMessageHtmlWithUrls(html, attachments, getTicketAttachmentUrl);
}

const attachmentDataUrlCache = new Map<string, string>();

/** Base64 data URL for embedding inline images in outbound HTML email. */
export async function getTicketAttachmentDataUrl(path: string): Promise<string> {
  const cached = attachmentDataUrlCache.get(path);
  if (cached) return cached;

  const { data: blob, error: dlErr } = await supabase.storage
    .from('crm-ticket-attachments')
    .download(path);
  if (dlErr || !blob) {
    throw new Error(formatSupabaseError(dlErr ?? { message: 'Download failed' }));
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read attachment'));
    reader.readAsDataURL(blob);
  });
  attachmentDataUrlCache.set(path, dataUrl);
  return dataUrl;
}

/** Resolve inline images as base64 data URLs so quoted content survives in sent email. */
export async function resolveTicketMessageHtmlForEmail(
  html: string,
  attachments: TicketAttachment[],
): Promise<string> {
  return resolveTicketMessageHtmlWithUrls(html, attachments, getTicketAttachmentDataUrl);
}

export async function getAttachmentUrlMap(
  attachments: TicketAttachment[],
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  await Promise.all(
    attachments.map(async (att) => {
      if (!att.storage_path) return;
      try {
        map.set(att.id, await getTicketAttachmentUrl(att.storage_path));
      } catch {
        /* skip */
      }
    }),
  );
  return map;
}

export async function findContactByEmail(email: string): Promise<{
  client_id: string | null;
  contact_id: string | null;
} | null> {
  const normalized = email.trim().toLowerCase();
  if (!normalized.includes('@')) return null;
  const db = demDb();
  const { data, error } = await db
    .from('contacts')
    .select('id,client_id,email')
    .ilike('email', normalized)
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return {
    client_id: data.client_id ? String(data.client_id) : null,
    contact_id: data.id ? String(data.id) : null,
  };
}

const attachmentBlobCache = new Map<string, string>();

export async function getTicketAttachmentUrl(path: string): Promise<string> {
  const cached = attachmentBlobCache.get(path);
  if (cached) return cached;

  const { data: blob, error: dlErr } = await supabase.storage
    .from('crm-ticket-attachments')
    .download(path);
  if (!dlErr && blob) {
    const objectUrl = URL.createObjectURL(blob);
    attachmentBlobCache.set(path, objectUrl);
    return objectUrl;
  }

  const { data, error } = await supabase.storage
    .from('crm-ticket-attachments')
    .createSignedUrl(path, 3600);
  if (error) throw new Error(formatSupabaseError(error));
  return data.signedUrl;
}

export function revokeTicketAttachmentUrls(): void {
  for (const url of attachmentBlobCache.values()) {
    URL.revokeObjectURL(url);
  }
  attachmentBlobCache.clear();
}

export async function updateTicket(
  id: string,
  patch: Partial<{
    status: TicketStatus;
    priority: TicketPriority;
    folder_id: string;
    client_id: string | null;
    contact_id: string | null;
    assignee_email: string;
    is_unread: boolean;
  }>,
): Promise<Ticket> {
  const db = demDb();
  const payload: Record<string, unknown> = { ...patch };
  // Postgres bigint columns: normalize empty → null, keep numeric string/number stable.
  if ('client_id' in payload) {
    const raw = payload.client_id;
    payload.client_id =
      raw == null || raw === '' ? null : Number.isFinite(Number(raw)) ? Number(raw) : raw;
  }
  if ('contact_id' in payload) {
    const raw = payload.contact_id;
    payload.contact_id =
      raw == null || raw === '' ? null : Number.isFinite(Number(raw)) ? Number(raw) : raw;
  }
  const { data, error } = await db
    .from('tickets')
    .update(payload)
    .eq('id', id)
    .select(TICKET_SELECT)
    .maybeSingle();
  if (error && isMissingFollowUpColumn(error)) {
    const fallback = await db
      .from('tickets')
      .update(payload)
      .eq('id', id)
      .select(TICKET_SELECT_BASE)
      .maybeSingle();
    if (fallback.error) throw new Error(formatSupabaseError(fallback.error));
    if (!fallback.data) throw new Error('Ticket update returned no rows.');
    return fallback.data as unknown as Ticket;
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Ticket update returned no rows.');
  return data as unknown as Ticket;
}

export async function logTicketActivity(
  ticketId: string,
  action: string,
  detail: Record<string, unknown> = {},
  actorEmail = '',
): Promise<void> {
  await logTicketActivityMany([ticketId], action, detail, actorEmail);
}

export async function logTicketActivityMany(
  ticketIds: string[],
  action: string,
  detail: Record<string, unknown> = {},
  actorEmail = '',
): Promise<void> {
  if (ticketIds.length === 0) return;
  const db = demDb();
  const { error } = await db.from('ticket_activity_log').insert(
    ticketIds.map((ticket_id) => ({
      ticket_id,
      actor_email: actorEmail,
      action,
      detail,
    })),
  );
  if (error) throw new Error(formatSupabaseError(error));
}

export async function markTicketSpam(ticketId: string, actorEmail: string): Promise<Ticket> {
  const folderId = await resolveSystemFolderId('spam');

  const ticket = await updateTicket(ticketId, {
    status: 'spam',
    folder_id: folderId,
    is_unread: false,
  });
  await logTicketActivity(ticketId, 'mark_spam', {}, actorEmail);
  return ticket;
}

export async function moveTicketToTrash(ticketId: string, actorEmail: string): Promise<Ticket> {
  await bulkMoveTicketsToTrash([ticketId], actorEmail);
  const ticket = await fetchTicket(ticketId);
  if (!ticket) throw new Error('Ticket not found after moving to Trash.');
  return ticket;
}

export async function bulkMoveTicketsToTrash(ids: string[], actorEmail: string): Promise<number> {
  if (ids.length === 0) return 0;
  const db = demDb();
  const folderId = await resolveSystemFolderId('trash');
  const { data, error } = await db
    .from('tickets')
    .update({ folder_id: folderId, is_unread: false })
    .in('id', ids)
    .select('id');
  if (error) throw new Error(formatSupabaseError(error));
  await logTicketActivityMany(ids, 'move_trash', { folder_id: folderId, bulk: true }, actorEmail);
  return data?.length ?? 0;
}

/** Permanently delete tickets (intended for Trash). Cascades messages/attachments rows. */
export async function permanentlyDeleteTickets(
  ids: string[],
  actorEmail = '',
): Promise<number> {
  if (ids.length === 0) return 0;
  const db = demDb();

  const { data: messages } = await db.from('ticket_messages').select('id').in('ticket_id', ids);
  const messageIds = (messages ?? []).map((m) => String(m.id));
  if (messageIds.length > 0) {
    const { data: atts } = await db
      .from('ticket_attachments')
      .select('storage_path')
      .in('message_id', messageIds);
    const paths = (atts ?? [])
      .map((a) => String(a.storage_path ?? '').trim())
      .filter(Boolean);
    if (paths.length > 0) {
      await supabase.storage.from('crm-ticket-attachments').remove(paths);
    }
  }

  await logTicketActivityMany(ids, 'permanent_delete', { bulk: true }, actorEmail);

  const { data, error } = await db.from('tickets').delete().in('id', ids).select('id');
  if (error) throw new Error(formatSupabaseError(error));
  return data?.length ?? 0;
}

export async function blockSender(
  email: string,
  actorEmail: string,
  ticketId?: string,
): Promise<void> {
  const pattern = email.trim().toLowerCase();
  if (!pattern.includes('@')) throw new Error('Invalid email to block.');
  const db = demDb();
  let mailboxId: string | null = null;
  if (ticketId) {
    const { data: ticketRow } = await db
      .from('tickets')
      .select('mailbox_id')
      .eq('id', ticketId)
      .maybeSingle();
    if (ticketRow?.mailbox_id) mailboxId = String(ticketRow.mailbox_id);
  }
  if (!mailboxId) mailboxId = await resolvePortalMailboxId();
  const row: Record<string, unknown> = {
    rule_type: 'email',
    pattern,
    action: 'block',
    is_active: true,
    created_by_email: actorEmail,
  };
  if (mailboxId) row.mailbox_id = mailboxId;
  let { error } = await db.from('ticket_spam_rules').insert(row);
  if (error && isMissingMailboxIdColumn(error) && mailboxId) {
    const { mailbox_id: _drop, ...legacy } = row;
    ({ error } = await db.from('ticket_spam_rules').insert(legacy));
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (ticketId) {
    await markTicketSpam(ticketId, actorEmail);
    await logTicketActivity(ticketId, 'block_sender', { email: pattern }, actorEmail);
  }
}

export async function fetchSpamRules(mailboxId: string): Promise<TicketSpamRule[]> {
  const db = demDb();
  const scoped = await db
    .from('ticket_spam_rules')
    .select('id,mailbox_id,rule_type,pattern,action,is_active,created_by_email,created_at')
    .eq('mailbox_id', mailboxId)
    .order('created_at', { ascending: false });
  if (!scoped.error) return (scoped.data ?? []) as TicketSpamRule[];
  if (!isMissingMailboxIdColumn(scoped.error)) throw new Error(formatSupabaseError(scoped.error));
  const { data, error } = await db
    .from('ticket_spam_rules')
    .select('id,rule_type,pattern,action,is_active,created_by_email,created_at')
    .order('created_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketSpamRule[];
}

export async function fetchTicketMailboxes(): Promise<TicketMailbox[]> {
  const db = demDb();
  const portal = resolveTicketPortalKind();
  const primary = await db
    .from('ticket_mailboxes')
    .select(MAILBOX_SELECT)
    .eq('portal_kind', portal)
    .order('address');
  if (primary.error && isMissingPortalKindColumn(primary.error)) {
    const legacy = await db
      .from('ticket_mailboxes')
      .select('id,address,display_name,transport,is_active')
      .order('address');
    if (legacy.error) throw new Error(formatSupabaseError(legacy.error));
    return ((legacy.data ?? []) as Array<Omit<TicketMailbox, 'portal_kind'>>).map((row) => ({
      ...row,
      portal_kind: 'crm' as const,
    }));
  }
  if (primary.error) throw new Error(formatSupabaseError(primary.error));
  return (primary.data ?? []) as TicketMailbox[];
}

/** Admin Settings: all mailboxes across CRM and IT (not filtered by active portal). */
export async function fetchAllTicketMailboxes(): Promise<TicketMailbox[]> {
  const db = demDb();
  const primary = await db
    .from('ticket_mailboxes')
    .select(MAILBOX_SELECT)
    .order('portal_kind')
    .order('address');
  if (primary.error && isMissingPortalKindColumn(primary.error)) {
    const legacy = await db
      .from('ticket_mailboxes')
      .select('id,address,display_name,transport,is_active')
      .order('address');
    if (legacy.error) throw new Error(formatSupabaseError(legacy.error));
    return ((legacy.data ?? []) as Array<Omit<TicketMailbox, 'portal_kind'>>).map((row) => ({
      ...row,
      portal_kind: 'crm' as const,
    }));
  }
  if (primary.error) throw new Error(formatSupabaseError(primary.error));
  return (primary.data ?? []) as TicketMailbox[];
}

export async function upsertTicketMailbox(input: {
  id?: string;
  address: string;
  display_name?: string;
  is_active?: boolean;
  portal_kind?: TicketPortalKind;
}): Promise<TicketMailbox> {
  const db = demDb();
  const portal = input.portal_kind ?? resolveTicketPortalKind();
  const row = {
    address: input.address.trim().toLowerCase(),
    display_name: (input.display_name ?? '').trim(),
    transport: 'graph' as const,
    is_active: input.is_active ?? true,
    portal_kind: portal,
    ...(input.id ? { id: input.id } : {}),
  };
  let { data, error } = await db
    .from('ticket_mailboxes')
    .upsert(row, { onConflict: 'id' })
    .select(MAILBOX_SELECT)
    .maybeSingle();
  if (error && isMissingPortalKindColumn(error)) {
    const { portal_kind: _drop, ...legacyRow } = row;
    ({ data, error } = await db
      .from('ticket_mailboxes')
      .upsert(legacyRow, { onConflict: 'id' })
      .select('id,address,display_name,transport,is_active')
      .maybeSingle());
    if (!error && data) {
      const result = { ...(data as Omit<TicketMailbox, 'portal_kind'>), portal_kind: 'crm' as const };
      if (!input.id) {
        await seedMailboxDefaults(String(result.id)).catch(() => undefined);
      }
      return result;
    }
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Mailbox save returned no rows.');
  if (!input.id) {
    await seedMailboxDefaults(String(data.id)).catch(() => undefined);
  }
  return data as TicketMailbox;
}

export async function deleteTicketMailbox(id: string): Promise<void> {
  const db = demDb();
  const { error } = await db.from('ticket_mailboxes').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchTicketAgents(): Promise<TicketAgent[]> {
  const db = demDb();
  const { data, error } = await db
    .from('ticket_agents')
    .select('id,email,auth_id,display_name,is_active')
    .order('email');
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as TicketAgent[];
}

export async function addTicketAgent(email: string, displayName = ''): Promise<TicketAgent> {
  const db = demDb();
  const normalized = email.trim().toLowerCase();
  const { data: existing } = await db
    .from('ticket_agents')
    .select('id,email,auth_id,display_name,is_active')
    .ilike('email', normalized)
    .maybeSingle();

  if (existing?.id) {
    const { data, error } = await db
      .from('ticket_agents')
      .update({
        is_active: true,
        display_name: displayName.trim() || String(existing.display_name ?? ''),
      })
      .eq('id', existing.id)
      .select('id,email,auth_id,display_name,is_active')
      .maybeSingle();
    if (error) throw new Error(formatSupabaseError(error));
    if (!data) throw new Error('Agent update returned no rows.');
    return data as TicketAgent;
  }

  const { data, error } = await db
    .from('ticket_agents')
    .insert({
      email: normalized,
      display_name: displayName.trim(),
      is_active: true,
    })
    .select('id,email,auth_id,display_name,is_active')
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Agent insert returned no rows.');
  return data as TicketAgent;
}

export async function setTicketAgentActive(id: string, isActive: boolean): Promise<void> {
  const db = demDb();
  const { error } = await db.from('ticket_agents').update({ is_active: isActive }).eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

/** Enable/disable ticketing access for a portal user email. */
export async function setTicketAgentByEmail(
  email: string,
  enabled: boolean,
  displayName = '',
): Promise<void> {
  const normalized = email.trim().toLowerCase();
  if (!normalized.includes('@')) throw new Error('Invalid email.');
  if (enabled) {
    await addTicketAgent(normalized, displayName);
    return;
  }
  const db = demDb();
  const { data: existing } = await db
    .from('ticket_agents')
    .select('id')
    .ilike('email', normalized)
    .maybeSingle();
  if (!existing?.id) return;
  await setTicketAgentActive(String(existing.id), false);
}

/** Active ticketing agent emails (lowercased). */
export async function fetchActiveTicketAgentEmails(): Promise<Set<string>> {
  const agents = await fetchTicketAgents();
  return new Set(
    agents.filter((a) => a.is_active).map((a) => a.email.trim().toLowerCase()),
  );
}

export async function uploadTicketAttachment(
  ticketId: string,
  file: File,
): Promise<{ path: string; file_name: string; content_type: string; size_bytes: number }> {
  const safeName = file.name.replace(/[^\w.\-()+ ]+/g, '_');
  const path = `${ticketId}/${Date.now()}_${safeName}`;
  const { error } = await supabase.storage.from('crm-ticket-attachments').upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return {
    path,
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
  };
}

export async function insertTicketMessageAttachment(
  messageId: string,
  file: { storage_path: string; file_name: string; content_type: string; size_bytes: number },
): Promise<TicketAttachment> {
  const db = demDb();
  const row = {
    message_id: messageId,
    storage_path: file.storage_path,
    file_name: file.file_name,
    content_type: file.content_type,
    size_bytes: file.size_bytes,
    is_inline: false,
    content_id: null,
  };
  const full = await db
    .from('ticket_attachments')
    .insert(row)
    .select(
      'id,message_id,storage_path,file_name,content_type,size_bytes,is_inline,content_id,created_at',
    )
    .maybeSingle();
  if (!full.error && full.data) return full.data as TicketAttachment;
  const fallback = await db
    .from('ticket_attachments')
    .insert({
      message_id: messageId,
      storage_path: file.storage_path,
      file_name: file.file_name,
      content_type: file.content_type,
      size_bytes: file.size_bytes,
    })
    .select('id,message_id,storage_path,file_name,content_type,size_bytes,created_at')
    .maybeSingle();
  if (fallback.error) throw new Error(formatSupabaseError(fallback.error));
  if (!fallback.data) throw new Error('Attachment insert returned no rows.');
  return fallback.data as TicketAttachment;
}

export async function sendTicketReply(input: {
  ticketId?: string;
  bodyHtml: string;
  bodyText?: string;
  toEmails?: string[];
  ccEmails?: string[];
  attachments?: Array<{ path: string; file_name: string; content_type: string; size_bytes: number }>;
  mode?: TicketComposeMode;
  subject?: string;
}): Promise<{ ok: boolean; message_id?: string; ticket_id?: string; error?: string }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in.');

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-ticket-send`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ticket_id: input.ticketId ?? '',
      body_html: input.bodyHtml,
      body_text: input.bodyText ?? '',
      to_emails: input.toEmails ?? [],
      cc_emails: input.ccEmails ?? [],
      attachments: input.attachments ?? [],
      mode: input.mode ?? 'reply',
      subject: input.subject ?? '',
    }),
  });

  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    message_id?: string;
    ticket_id?: string;
    error?: string;
    message?: string;
    code?: string;
  };
  if (!res.ok || !payload.ok) {
    const fromBody =
      (typeof payload.error === 'string' && payload.error) ||
      (typeof payload.message === 'string' && payload.message) ||
      (typeof payload.code === 'string' && payload.code) ||
      '';
    if (res.status === 546) {
      throw new Error(
        fromBody ||
          'Send failed: Edge Function ran out of memory/CPU (HTTP 546). Try a shorter reply, fewer attachments, or ask IT to redeploy crm-ticket-send.',
      );
    }
    throw new Error(fromBody || `Send failed (HTTP ${res.status})`);
  }
  return { ok: true, message_id: payload.message_id, ticket_id: payload.ticket_id };
}

export async function triggerTicketInboxSync(): Promise<Record<string, unknown>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in.');

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-ticket-inbox-sync`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || payload.ok === false) {
    throw new Error(String(payload.error ?? `Sync failed (HTTP ${res.status})`));
  }
  return payload;
}

export async function fetchTicketEmailTemplate(
  slug: string,
  mailboxId: string,
): Promise<TicketEmailTemplate | null> {
  const db = demDb();
  const scoped = await db
    .from('ticket_email_templates')
    .select('id,mailbox_id,slug,name,subject_template,body_html_template,is_active,updated_at')
    .eq('mailbox_id', mailboxId)
    .eq('slug', slug)
    .maybeSingle();
  if (!scoped.error) return (scoped.data as TicketEmailTemplate | null) ?? null;
  if (!isMissingMailboxIdColumn(scoped.error)) throw new Error(formatSupabaseError(scoped.error));
  const { data, error } = await db
    .from('ticket_email_templates')
    .select('slug,name,subject_template,body_html_template,is_active,updated_at')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  return (data as TicketEmailTemplate | null) ?? null;
}

export type ReplySignature = {
  slug: string;
  name: string;
  html: string;
  isActive: boolean;
  /** Legacy primary row (`reply_signature`). */
  isDefault: boolean;
};

function isReplySignatureSlug(slug: string): boolean {
  return slug === 'reply_signature' || slug.startsWith('rsig_');
}

/** Same signature for Settings and ticket Reply: DB first, else the Reply draft in this browser. */
export async function resolveReplySignature(
  mailboxId?: string,
): Promise<{ html: string; isActive: boolean }> {
  const id = mailboxId ?? (await resolvePortalMailboxId());
  if (!id) return { html: loadTicketEmailSignature(), isActive: true };
  const list = await listReplySignatures(id);
  const primary = list.find((s) => s.isDefault) ?? list[0];
  if (primary?.html) saveTicketEmailSignature(primary.html);
  return { html: primary?.html ?? '', isActive: primary?.isActive ?? true };
}

export async function listReplySignatures(mailboxId: string): Promise<ReplySignature[]> {
  const db = demDb();
  const scoped = await db
    .from('ticket_email_templates')
    .select('id,mailbox_id,slug,name,subject_template,body_html_template,is_active,updated_at')
    .eq('mailbox_id', mailboxId)
    .or('slug.eq.reply_signature,slug.like.rsig_%')
    .order('updated_at', { ascending: true });
  let rows: TicketEmailTemplate[] = [];
  if (!scoped.error) {
    rows = ((scoped.data ?? []) as TicketEmailTemplate[]).filter((r) =>
      isReplySignatureSlug(r.slug),
    );
  } else if (isMissingMailboxIdColumn(scoped.error)) {
    const { data, error } = await db
      .from('ticket_email_templates')
      .select('slug,name,subject_template,body_html_template,is_active,updated_at')
      .or('slug.eq.reply_signature,slug.like.rsig_%')
      .order('updated_at', { ascending: true });
    if (error) throw new Error(formatSupabaseError(error));
    rows = ((data ?? []) as TicketEmailTemplate[]).filter((r) => isReplySignatureSlug(r.slug));
  } else {
    throw new Error(formatSupabaseError(scoped.error));
  }

  if (rows.length === 0) {
    const local = loadTicketEmailSignature();
    if (local) {
      await upsertTicketEmailTemplate({
        mailbox_id: mailboxId,
        slug: 'reply_signature',
        name: 'Default',
        subject_template: '',
        body_html_template: local,
        is_active: true,
      });
      return [
        {
          slug: 'reply_signature',
          name: 'Default',
          html: local,
          isActive: true,
          isDefault: true,
        },
      ];
    }
    return [];
  }

  return rows.map((r) => ({
    slug: r.slug,
    name: r.name || (r.slug === 'reply_signature' ? 'Default' : r.slug),
    html: (r.body_html_template ?? '').trim(),
    isActive: r.is_active !== false,
    isDefault: r.slug === 'reply_signature',
  }));
}

export async function saveReplySignature(input: {
  mailbox_id: string;
  slug?: string;
  name: string;
  html: string;
  isActive?: boolean;
}): Promise<ReplySignature> {
  const name = input.name.trim() || 'Signature';
  const html = input.html.trim();
  let slug = (input.slug ?? '').trim();
  if (!slug) {
    slug = `rsig_${Date.now().toString(36)}`;
  } else if (!isReplySignatureSlug(slug)) {
    throw new Error('Invalid signature slug.');
  }
  const saved = await upsertTicketEmailTemplate({
    mailbox_id: input.mailbox_id,
    slug,
    name,
    subject_template: '',
    body_html_template: html,
    is_active: input.isActive ?? true,
  });
  if (slug === 'reply_signature') saveTicketEmailSignature(html);
  return {
    slug: saved.slug,
    name: saved.name,
    html: (saved.body_html_template ?? '').trim(),
    isActive: saved.is_active !== false,
    isDefault: saved.slug === 'reply_signature',
  };
}

export async function deleteReplySignature(slug: string, mailboxId: string): Promise<void> {
  if (slug === 'reply_signature') {
    throw new Error('Cannot delete the default signature. Clear or replace its content instead.');
  }
  if (!slug.startsWith('rsig_')) {
    throw new Error('Invalid signature slug.');
  }
  const db = demDb();
  let { error } = await db
    .from('ticket_email_templates')
    .delete()
    .eq('mailbox_id', mailboxId)
    .eq('slug', slug);
  if (error && isMissingMailboxIdColumn(error)) {
    ({ error } = await db.from('ticket_email_templates').delete().eq('slug', slug));
  }
  if (error) throw new Error(formatSupabaseError(error));
}

export async function mergeTickets(
  targetId: string,
  sourceId: string,
  actorEmail: string,
): Promise<Ticket> {
  if (!targetId || !sourceId) throw new Error('Both tickets are required.');
  if (targetId === sourceId) throw new Error('Cannot merge a ticket into itself.');

  const [target, source] = await Promise.all([fetchTicket(targetId), fetchTicket(sourceId)]);
  if (!target) throw new Error('Target ticket not found.');
  if (!source) throw new Error('Source ticket not found.');

  const db = demDb();
  const { error: moveMsgErr } = await db
    .from('ticket_messages')
    .update({ ticket_id: targetId })
    .eq('ticket_id', sourceId);
  if (moveMsgErr) throw new Error(formatSupabaseError(moveMsgErr));

  const { error: moveActErr } = await db
    .from('ticket_activity_log')
    .update({ ticket_id: targetId })
    .eq('ticket_id', sourceId);
  if (moveActErr && !/relation|does not exist/i.test(formatSupabaseError(moveActErr))) {
    throw new Error(formatSupabaseError(moveActErr));
  }

  const { data: sourceTags } = await db
    .from('ticket_tag_links')
    .select('tag_id')
    .eq('ticket_id', sourceId);
  const sourceTagIds = (sourceTags ?? []).map((r) => String(r.tag_id));
  if (sourceTagIds.length > 0) {
    const { data: targetTags } = await db
      .from('ticket_tag_links')
      .select('tag_id')
      .eq('ticket_id', targetId);
    const existing = new Set((targetTags ?? []).map((r) => String(r.tag_id)));
    const toAdd = sourceTagIds.filter((id) => !existing.has(id));
    if (toAdd.length > 0) {
      await db.from('ticket_tag_links').insert(
        toAdd.map((tag_id) => ({ ticket_id: targetId, tag_id })),
      );
    }
    await db.from('ticket_tag_links').delete().eq('ticket_id', sourceId);
  }

  const lastAt =
    target.last_message_at && source.last_message_at
      ? target.last_message_at > source.last_message_at
        ? target.last_message_at
        : source.last_message_at
      : target.last_message_at || source.last_message_at;

  const patch: Record<string, unknown> = {
    last_message_at: lastAt || new Date().toISOString(),
    is_unread: Boolean(target.is_unread || source.is_unread),
  };
  if (!target.conversation_key?.trim() && source.conversation_key?.trim()) {
    patch.conversation_key = source.conversation_key;
  }
  if (!target.client_id && source.client_id) {
    patch.client_id = source.client_id;
    if (!target.contact_id && source.contact_id) patch.contact_id = source.contact_id;
  }

  const { error: updErr } = await db.from('tickets').update(patch).eq('id', targetId);
  if (updErr) throw new Error(formatSupabaseError(updErr));

  await logTicketActivity(
    targetId,
    'merge_ticket',
    {
      source_id: sourceId,
      source_number: source.ticket_number,
      source_subject: source.subject,
    },
    actorEmail,
  );

  await moveTicketToTrash(sourceId, actorEmail);

  const merged = await fetchTicket(targetId);
  if (!merged) throw new Error('Merged ticket not found.');
  return merged;
}

export type TicketWorkflowSettings = {
  auto_reopen_on_inbound: boolean;
  /** 0 = disabled. Default 2 Singapore working days. */
  follow_up_workdays: number;
};

/** Count Mon–Fri days strictly after `from` up to (and including) `to` calendar day in local time. */
export function countWorkingDaysBetween(fromIso: string, toDate = new Date()): number {
  const start = new Date(fromIso);
  if (Number.isNaN(start.getTime())) return 0;
  const from = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const to = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  if (to <= from) return 0;
  let count = 0;
  const cursor = new Date(from);
  cursor.setDate(cursor.getDate() + 1);
  while (cursor <= to) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function ticketNeedsFollowUp(
  ticket: Pick<Ticket, 'status' | 'last_message_at'> & { last_follow_up_at?: string | null },
  workdays: number,
): boolean {
  if (workdays <= 0) return false;
  const closedLike = /^(closed|spam|trash)$/i.test(ticket.status);
  if (closedLike) return false;
  const anchor = ticket.last_follow_up_at || ticket.last_message_at;
  if (!anchor) return false;
  return countWorkingDaysBetween(anchor) >= workdays;
}

export async function fetchTicketWorkflowSettings(
  mailboxId: string,
): Promise<TicketWorkflowSettings> {
  const db = demDb();
  const scoped = await db
    .from('ticket_settings')
    .select('auto_reopen_on_inbound,follow_up_workdays')
    .eq('mailbox_id', mailboxId)
    .maybeSingle();
  if (!scoped.error) {
    return {
      auto_reopen_on_inbound: scoped.data?.auto_reopen_on_inbound !== false,
      follow_up_workdays: Number(scoped.data?.follow_up_workdays ?? 2) || 0,
    };
  }
  if (isMissingMailboxIdColumn(scoped.error)) {
    const { data, error } = await db
      .from('ticket_settings')
      .select('auto_reopen_on_inbound,follow_up_workdays')
      .eq('id', 1)
      .maybeSingle();
    if (error) {
      if (/follow_up_workdays|column/i.test(formatSupabaseError(error))) {
        const fallback = await db
          .from('ticket_settings')
          .select('auto_reopen_on_inbound')
          .eq('id', 1)
          .maybeSingle();
        if (fallback.error) throw new Error(formatSupabaseError(fallback.error));
        return {
          auto_reopen_on_inbound: fallback.data?.auto_reopen_on_inbound !== false,
          follow_up_workdays: 2,
        };
      }
      throw new Error(formatSupabaseError(error));
    }
    return {
      auto_reopen_on_inbound: data?.auto_reopen_on_inbound !== false,
      follow_up_workdays: Number(data?.follow_up_workdays ?? 2) || 0,
    };
  }
  if (/follow_up_workdays|column/i.test(formatSupabaseError(scoped.error))) {
    const fallback = await db
      .from('ticket_settings')
      .select('auto_reopen_on_inbound')
      .eq('mailbox_id', mailboxId)
      .maybeSingle();
    if (fallback.error && isMissingMailboxIdColumn(fallback.error)) {
      return { auto_reopen_on_inbound: true, follow_up_workdays: 2 };
    }
    if (fallback.error) throw new Error(formatSupabaseError(fallback.error));
    return {
      auto_reopen_on_inbound: fallback.data?.auto_reopen_on_inbound !== false,
      follow_up_workdays: 2,
    };
  }
  throw new Error(formatSupabaseError(scoped.error));
}

export async function saveTicketWorkflowSettings(
  input: TicketWorkflowSettings,
  mailboxId: string,
): Promise<TicketWorkflowSettings> {
  const db = demDb();
  const row = {
    mailbox_id: mailboxId,
    auto_reopen_on_inbound: Boolean(input.auto_reopen_on_inbound),
    follow_up_workdays: Math.max(0, Math.min(30, Number(input.follow_up_workdays) || 0)),
    updated_at: new Date().toISOString(),
  };
  let { data, error } = await db
    .from('ticket_settings')
    .upsert(row, { onConflict: 'mailbox_id' })
    .select('auto_reopen_on_inbound,follow_up_workdays')
    .maybeSingle();
  if (error && isMissingMailboxIdColumn(error)) {
    const legacy = {
      id: 1,
      auto_reopen_on_inbound: row.auto_reopen_on_inbound,
      follow_up_workdays: row.follow_up_workdays,
      updated_at: row.updated_at,
    };
    ({ data, error } = await db
      .from('ticket_settings')
      .upsert(legacy, { onConflict: 'id' })
      .select('auto_reopen_on_inbound,follow_up_workdays')
      .maybeSingle());
  }
  if (error && /null value.*id|id.*not-null|column.*id/i.test(formatSupabaseError(error))) {
    const maxRow = await db
      .from('ticket_settings')
      .select('id')
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();
    const nextId = Number(maxRow.data?.id ?? 0) + 1;
    ({ data, error } = await db
      .from('ticket_settings')
      .upsert(
        { ...row, id: nextId },
        { onConflict: 'mailbox_id' },
      )
      .select('auto_reopen_on_inbound,follow_up_workdays')
      .maybeSingle());
  }
  if (error) throw new Error(formatSupabaseError(error));
  return {
    auto_reopen_on_inbound: data?.auto_reopen_on_inbound !== false,
    follow_up_workdays: Number(data?.follow_up_workdays ?? row.follow_up_workdays) || 0,
  };
}

export async function sendTicketFollowUpReminder(
  ticket: Ticket,
  actorEmail: string,
  options?: { notifyOnly?: boolean },
): Promise<void> {
  const db = demDb();

  if (!options?.notifyOnly) {
    const mailboxId =
      ticket.mailbox_id || (await resolvePortalMailboxId()) || '';
    const tpl = mailboxId
      ? await fetchTicketEmailTemplate('follow_up_ack', mailboxId)
      : null;
    if (tpl && tpl.is_active === false) {
      throw new Error('Follow-up email template is turned off in Settings.');
    }
    const toCustomer = (ticket.requester_email ?? '').trim();
    const toAssignee = (ticket.assignee_email ?? '').trim();
    const recipients = [toCustomer, toAssignee].filter((e) => e.includes('@'));
    if (recipients.length === 0) {
      throw new Error('No customer or assignee email to remind.');
    }
    const unique = [...new Set(recipients.map((e) => e.toLowerCase()))];
    const rendered = tpl
      ? previewOpeningAckHtml(tpl.subject_template, tpl.body_html_template, {
          ticket_number: ticket.ticket_number,
          requester_name: ticket.requester_name || 'Customer',
          subject: ticket.subject,
        })
      : {
          subject: `Follow-up: ${ticket.subject || ticket.ticket_number} [${ticket.ticket_number}]`,
          html: `
    <p>Dear ${ticket.requester_name || 'Customer'},</p>
    <p>This is a follow-up on ticket <strong>${ticket.ticket_number}</strong>: ${ticket.subject || '(no subject)'}.</p>
    <p>Please reply to this email if you need further assistance. Our team is ready to help.</p>
    <p>Best regards,<br>Ban Teck Han Support</p>
  `,
        };
    await sendTicketReply({
      ticketId: ticket.id,
      bodyHtml: rendered.html,
      subject: rendered.subject,
      toEmails: unique,
      mode: 'reply',
    });
    await logTicketActivity(ticket.id, 'follow_up_sent', { to: unique }, actorEmail);
  }

  const { error: updErr } = await db
    .from('tickets')
    .update({ last_follow_up_at: new Date().toISOString() })
    .eq('id', ticket.id);
  if (updErr && !/last_follow_up_at|column/i.test(formatSupabaseError(updErr))) {
    throw new Error(formatSupabaseError(updErr));
  }
  if (options?.notifyOnly) {
    await logTicketActivity(ticket.id, 'follow_up_reminded', {}, actorEmail);
  }
}

export async function sendClosingAckIfEnabled(ticket: Ticket): Promise<void> {
  const mailboxId = ticket.mailbox_id || (await resolvePortalMailboxId()) || '';
  if (!mailboxId) return;
  const tpl = await fetchTicketEmailTemplate('closing_ack', mailboxId);
  if (!tpl?.is_active) return;
  const to = (ticket.requester_email ?? '').trim();
  if (!to.includes('@')) return;
  const rendered = previewOpeningAckHtml(tpl.subject_template, tpl.body_html_template, {
    ticket_number: ticket.ticket_number,
    requester_name: ticket.requester_name || 'Customer',
    subject: ticket.subject,
  });
  await sendTicketReply({
    ticketId: ticket.id,
    bodyHtml: rendered.html,
    subject: rendered.subject,
    toEmails: [to],
    mode: 'reply',
  });
}

export async function upsertTicketEmailTemplate(input: {
  mailbox_id: string;
  id?: string;
  slug: string;
  name: string;
  subject_template: string;
  body_html_template: string;
  is_active?: boolean;
}): Promise<TicketEmailTemplate> {
  const db = demDb();
  const row = {
    mailbox_id: input.mailbox_id,
    slug: input.slug.trim(),
    name: input.name.trim(),
    subject_template: input.subject_template,
    body_html_template: input.body_html_template,
    is_active: input.is_active ?? true,
    updated_at: new Date().toISOString(),
    ...(input.id ? { id: input.id } : {}),
  };
  const onConflict = input.id ? 'id' : 'mailbox_id,slug';
  let { data, error } = await db
    .from('ticket_email_templates')
    .upsert(row, { onConflict })
    .select('id,mailbox_id,slug,name,subject_template,body_html_template,is_active,updated_at')
    .maybeSingle();
  if (error && isMissingMailboxIdColumn(error)) {
    const { mailbox_id: _drop, id: _id, ...legacy } = row;
    ({ data, error } = await db
      .from('ticket_email_templates')
      .upsert(legacy, { onConflict: 'slug' })
      .select('slug,name,subject_template,body_html_template,is_active,updated_at')
      .maybeSingle());
  }
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Template save returned no rows.');
  return data as TicketEmailTemplate;
}

export async function bulkUpdateTickets(
  ids: string[],
  patch: Partial<{
    status: TicketStatus;
    priority: TicketPriority;
    folder_id: string;
    is_unread: boolean;
  }>,
): Promise<number> {
  if (ids.length === 0) return 0;
  const db = demDb();
  const { data, error } = await db.from('tickets').update(patch).in('id', ids).select('id');
  if (error) throw new Error(formatSupabaseError(error));
  return data?.length ?? 0;
}

export async function bulkMarkTicketsSpam(ids: string[], actorEmail: string): Promise<number> {
  if (ids.length === 0) return 0;
  const db = demDb();
  const folderId = await resolveSystemFolderId('spam');
  const { data, error } = await db
    .from('tickets')
    .update({ status: 'spam', folder_id: folderId, is_unread: false })
    .in('id', ids)
    .select('id');
  if (error) throw new Error(formatSupabaseError(error));
  await logTicketActivityMany(ids, 'mark_spam', { bulk: true }, actorEmail);
  return data?.length ?? 0;
}

export async function fetchTicketNumberConfig(
  portalKind?: TicketPortalKind,
): Promise<TicketNumberConfig> {
  const db = demDb();
  const portal = portalKind ?? resolveTicketPortalKind();
  let { data, error } = await db.rpc('ticket_number_config', { p_portal_kind: portal });
  if (error && /p_portal_kind|function|argument/i.test(error.message)) {
    ({ data, error } = await db.rpc('ticket_number_config'));
  }
  if (error) throw new Error(formatSupabaseError(error));
  const row = (data ?? {}) as TicketNumberConfig;
  const defaultPrefix = portal === 'it' ? 'BTH-IT' : 'BTH-ENQ';
  return {
    prefix: String(row.prefix ?? defaultPrefix),
    pad_width: Number(row.pad_width) || 4,
    period: String(row.period ?? ''),
    last_seq: Number(row.last_seq) || 0,
    next_seq: Number(row.next_seq) || 1,
    preview: String(row.preview ?? ''),
  };
}

export async function saveTicketNumberConfig(input: {
  prefix: string;
  nextSeq: number;
  padWidth?: number;
  portalKind?: TicketPortalKind;
}): Promise<TicketNumberConfig> {
  const db = demDb();
  const portal = input.portalKind ?? resolveTicketPortalKind();
  let { data, error } = await db.rpc('save_ticket_number_config', {
    p_prefix: input.prefix.trim(),
    p_next_seq: input.nextSeq,
    p_pad_width: input.padWidth ?? 4,
    p_portal_kind: portal,
  });
  if (error && /p_portal_kind|function|argument/i.test(error.message)) {
    ({ data, error } = await db.rpc('save_ticket_number_config', {
      p_prefix: input.prefix.trim(),
      p_next_seq: input.nextSeq,
      p_pad_width: input.padWidth ?? 4,
    }));
  }
  if (error) throw new Error(formatSupabaseError(error));
  const row = (data ?? {}) as TicketNumberConfig;
  return {
    prefix: String(row.prefix ?? input.prefix),
    pad_width: Number(row.pad_width) || 4,
    period: String(row.period ?? ''),
    last_seq: Number(row.last_seq) || 0,
    next_seq: Number(row.next_seq) || 1,
    preview: String(row.preview ?? ''),
  };
}
